import { GroupStats, AgencyStatsTotals, DailyGroupPrompts } from '../../../shared/models/agency-stats/AgencyStatsResponse';
import { GroupDelta, UsageScore, StatusAlert, GroupInsights, EnrichedGroupStats } from '../models/AgencyStatsDerived';

export function computeGroupDeltas(
  current: GroupStats[],
  previous: GroupStats[]
): Map<number | null, GroupDelta> {
  const prevMap = new Map<number | null, GroupStats>();
  for (const g of previous) prevMap.set(g.groupId, g);

  const result = new Map<number | null, GroupDelta>();
  for (const g of current) {
    const prev = prevMap.get(g.groupId);
    if (!prev) {
      result.set(g.groupId, {
        promptsDelta: g.totalPrompts,
        promptsDeltaPercent: null,
        projectsDelta: g.projectCount,
        projectsDeltaPercent: null,
      });
    } else {
      result.set(g.groupId, {
        promptsDelta: g.totalPrompts - prev.totalPrompts,
        promptsDeltaPercent: prev.totalPrompts > 0
          ? Math.round(((g.totalPrompts - prev.totalPrompts) / prev.totalPrompts) * 100)
          : null,
        projectsDelta: g.projectCount - prev.projectCount,
        projectsDeltaPercent: prev.projectCount > 0
          ? Math.round(((g.projectCount - prev.projectCount) / prev.projectCount) * 100)
          : null,
      });
    }
  }
  return result;
}

export function computeUsageScore(group: GroupStats, totals: AgencyStatsTotals): UsageScore {
  const share = totals.totalPrompts > 0 ? group.totalPrompts / totals.totalPrompts : 0;
  if (share >= 0.2 && group.activePrompts >= 10) return 'HIGH';
  if (share >= 0.05 || group.activePrompts >= 5) return 'MEDIUM';
  return 'LOW';
}

export function computeShareOfTotal(group: GroupStats, totalPrompts: number): number {
  if (totalPrompts === 0) return 0;
  return Math.round((group.totalPrompts / totalPrompts) * 1000) / 10; // one decimal
}

export function computeNewExisting(group: GroupStats) {
  return {
    newPrompts: group.addedPromptsInRange,
    existingPrompts: Math.max(0, group.activePrompts - group.addedPromptsInRange),
    deleted: group.deletedProjectsInRange,
  };
}

export function computeStatusAlert(group: GroupStats, delta: GroupDelta | undefined): StatusAlert {
  if (delta) {
    if (delta.promptsDeltaPercent !== null && delta.promptsDeltaPercent > 50) return 'HIGH_GROWTH';
    if (delta.promptsDelta > 20) return 'SPIKE';
  }
  if (group.activePrompts < 3) return 'LOW_ACTIVITY';
  return null;
}

export function computeInsights(
  groups: GroupStats[],
  deltas: Map<number | null, GroupDelta>
): GroupInsights {
  // Only consider named groups (not ungrouped)
  const named = groups.filter((g) => g.groupId !== null);

  let fastestGrowing: GroupInsights['fastestGrowing'] = null;
  let highestUsage: GroupStats | null = null;
  let lowestActivity: GroupStats | null = null;

  for (const g of named) {
    const delta = deltas.get(g.groupId);
    if (delta?.promptsDeltaPercent !== null && delta?.promptsDeltaPercent !== undefined) {
      if (!fastestGrowing || delta.promptsDeltaPercent > fastestGrowing.deltaPercent) {
        fastestGrowing = { group: g, deltaPercent: delta.promptsDeltaPercent };
      }
    }
    if (!highestUsage || g.totalPrompts > highestUsage.totalPrompts) {
      highestUsage = g;
    }
    if (!lowestActivity || g.activePrompts < lowestActivity.activePrompts) {
      lowestActivity = g;
    }
  }

  return { fastestGrowing, highestUsage, lowestActivity };
}

export function computeDailyAverage(totalPrompts: number, rangeDays: number): number {
  if (rangeDays === 0) return 0;
  return Math.round((totalPrompts / rangeDays) * 10) / 10;
}

export function extractSparklineData(
  groupId: number | null,
  dailyGroupPrompts: DailyGroupPrompts[]
): number[] {
  return dailyGroupPrompts.map((day) => {
    const entry = day.groups.find((g) => g.groupId === groupId);
    return entry?.count ?? 0;
  });
}

export function enrichGroups(
  groups: GroupStats[],
  previousGroups: GroupStats[],
  totals: AgencyStatsTotals,
  dailyGroupPrompts: DailyGroupPrompts[],
  rangeDays: number
): EnrichedGroupStats[] {
  const deltas = computeGroupDeltas(groups, previousGroups);
  return groups.map((g) => {
    const delta = deltas.get(g.groupId);
    return {
      ...g,
      delta,
      usageScore: computeUsageScore(g, totals),
      shareOfTotal: computeShareOfTotal(g, totals.totalPrompts),
      newExisting: computeNewExisting(g),
      statusAlert: computeStatusAlert(g, delta),
      dailyAverage: computeDailyAverage(g.totalPrompts, rangeDays),
      sparklineData: extractSparklineData(g.groupId, dailyGroupPrompts),
    };
  });
}
